import { FILE_STATUS, FILE_TYPE, PLAYBACK_STATE, PLAYER_TYPE, SOURCE_FILE_TYPES } from '../enums'
import * as ActionTypes from './ActionTypes'
import * as API from '~/ServerAPI'
import * as Selectors from '~/selectors'
import { PREVIEW_MODE } from '../config/constants/preview'
import { addSourceFileAtSliderPosition } from './addSourceFileAtSliderPosition'
import { changeTimelinePlaybackState, resetPlaybackClip } from './playback'
import { clearAssetsSelection, selectAsset, updateAssetInPreview } from './layer'
import { deleteAssets } from './timeline'
import { localStorageLastCapturedLiveStreamURL } from '../constant'
import { parseLoadedFile } from '../Util/sourceFile'
import { saveProject } from './projectData/saveProject'
import { setActivePreview, setPreviewMode } from './preview'
import { setRecordingStatus } from '../reducers/recording'
import { updateSourceFile, updateSourceFileStatus } from './sourceFiles'
import { HistoryActionsService } from '~/services/HistoryActionsService'

let historyAction
let controller
let captureId
let assetId

export const getLastCapturedURL = () => localStorage.getItem(localStorageLastCapturedLiveStreamURL) ?? ''

export const isCapturedAsset = asset => asset.type === FILE_TYPE.LIVE_STREAM
  && (asset.id === assetId || asset.fileId === captureId)

export const importLiveStreamFromUrl = url => async (_dispatch, getState) => {
  let response = null
  const folder = Selectors.selectCurrentFolderId(getState(), SOURCE_FILE_TYPES.MEDIA)

  try {
    response = await API.importLiveStreamFromUrl({ url, folder })
  } catch (err) {
    response = err.response
  }

  if (response?.data?.id !== undefined) {
    localStorage.setItem(localStorageLastCapturedLiveStreamURL, String(url))
    captureId = response.data.id
    return response.data.id
  }

  return undefined
}

export const startLiveCapture = id => async (dispatch, getState) => {
  if (!id) return

  try {
    controller = API.getCaptureLiveStream(id, async data => {
      const item = parseLoadedFile(SOURCE_FILE_TYPES.MEDIA, data)
      const { duration } = item

      if (assetId) {
        const asset = Selectors.selectAssetById(getState(), assetId)
        if (duration > asset?.duration) {
          dispatch(updateAssetInPreview(assetId, { mediaFileDuration: duration }))
          dispatch(updateSourceFile(item))

          if (historyAction) {
            if (Selectors.history.isHistoryActionExists(historyAction.id, getState())) {
              const asset = Selectors.selectAssetById(getState(), assetId)
              historyAction.asset = asset
              historyAction.payload = { ...historyAction.payload, asset }
            }
          }
        }
      } else if (duration > 0) {
        await dispatch(clearAssetsSelection())
        await dispatch(setRecordingStatus('capture'))
        dispatch({
          type: ActionTypes.SOURCE_FILE_CREATED,
          payload: { item },
        })
        const assets = dispatch(addSourceFileAtSliderPosition(id))
        assetId = assets[0]?.id
        historyAction = Selectors.history.selectCurrentHistoryAction(getState())
        dispatch(selectAsset(assetId))
        await dispatch(setPreviewMode(PREVIEW_MODE.AUTO))
        await dispatch(setActivePreview(PLAYER_TYPE.TIMELINE))
        await dispatch(changeTimelinePlaybackState(PLAYBACK_STATE.PLAY))
      }
    })
  } catch (e) {
    controller.abort()
    await dispatch(setRecordingStatus('error'))
    throw Error(e || 'Start live stream capture error')
  }
}

export const stopLiveCapture = () => async dispatch => {
  controller && controller.abort()

  if (captureId) {
    try {
      dispatch(changeTimelinePlaybackState(PLAYBACK_STATE.STOP))
      dispatch(setRecordingStatus('stop'))
      dispatch(updateSourceFileStatus({ id: captureId, progress: 100, status: FILE_STATUS.READY }))
      await API.deleteLiveStream(captureId)
      dispatch(saveProject())
      captureId = undefined
      assetId = undefined
      controller = undefined
      historyAction = undefined
    } catch (e) {
      throw Error(e || 'Stop live stream capture error')
    }
  }
}

export const deleteLiveStream = () => async (dispatch, getState) => {
  controller && controller.abort()

  if (captureId && assetId) {
    dispatch({
      type: ActionTypes.ACTIONS_CLEAR_ACTIONS_HISTORY,
      payload: { actionIndex: getState().historyActions.currentActionIndex },
    })

    try {
      dispatch(changeTimelinePlaybackState(PLAYBACK_STATE.STOP))
      dispatch(resetPlaybackClip())
      dispatch(setRecordingStatus('stop'))
      const asset = Selectors.selectAssetById(getState(), assetId)
      if (asset) {
        await dispatch(deleteAssets([ asset ]))
      }
      dispatch({
        type: ActionTypes.SOURCE_FILE_DELETED,
        payload: { id: captureId, type: SOURCE_FILE_TYPES.MEDIA },
      })
      await API.deleteLiveStream(captureId, false)
      await dispatch(saveProject())
      captureId = undefined
      assetId = undefined
      controller = undefined
      historyAction = undefined
    } catch (e) {
      throw Error(e || 'Stop live stream capture error')
    }
  }
}

export const removeLiveStream = (sync = true) => async (dispatch, getState) => {
  let nextHistoryAction
  const state = getState()
  const isPointerAtFirstAction = Selectors.history.selectIsPointerAtFirstAction(state)
  const isLatestActionIsHistory = Selectors.history.selectIsLatestActionIsHistory(state)
  const isLockedHistory = Selectors.history.selectIsLockedHistory(state)
  if (!isLatestActionIsHistory && !isLockedHistory) {
    await dispatch({ type: ActionTypes.ACTIONS_UNDO })
    if (!isPointerAtFirstAction) {
      nextHistoryAction = Selectors.history.selectNextHistoryAction(getState())
      if (nextHistoryAction) {
        const undoActionsGenerator = nextHistoryAction.undo()
        for (const action of undoActionsGenerator) {
          // eslint-disable-next-line no-await-in-loop
          await dispatch(action)
        }
        await dispatch({ type: ActionTypes.ACTIONS_CLEAR_IGNORE_HISTORY })
        if (sync) {
          HistoryActionsService.undoAction(nextHistoryAction.id)
        }
      }
    }
    await dispatch({ type: ActionTypes.ACTIONS_UNLOCK_ACTIONS_HISTORY })
    if (nextHistoryAction?.isTransit) {
      dispatch(removeLiveStream())
    }
  }
  if (getState().recording.recordingStatus === 'capture') {
    dispatch(deleteLiveStream())
  }
}
